import {
  Component,
  signal,
  inject,
  OnInit,
  ViewChild,
  effect,
  computed,
  untracked,
  WritableSignal,
  DestroyRef,
  input,
  ChangeDetectionStrategy
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  CdkDragDrop,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SlidePanelComponent } from '../../../../shared/components/slide-panel/slide-panel.component';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { CommonModule } from '@angular/common';
import { PredefinedRange } from '../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../core/services/api.service';
import {
  DropdownComponent,
  DropdownOption,
} from '../../../../shared/components/ui/dropdown/dropdown.component';
import { InputComponent } from '../../../../shared/components/ui/input/input.component';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import {
  TableHeader,
  PaginatedTableComponent,
} from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import {
  IconComponent,
  IconName,
} from '../../../../shared/icons/icon.component';
import { ThemeService } from '../../../../core/services/theme.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const LA_TIMEZONE = 'America/Los_Angeles';
import { DonutChartComponent } from '../../../../shared/components/charts/donut-chart/donut-chart.component';
import {
  MultiBarData,
  MultiHorizontalBarChartComponent,
} from '../../../../shared/components/charts/multi-horizontal-bar-chart/multi-horizontal-bar-chart.component';
import { MultiLineChartComponent } from '../../../../shared/components/charts/multi-line-chart/multi-line-chart.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { WatchTimeChartComponent } from '../../../../shared/components/charts/watchTime-chart/watch-time-chart.component';
import { ToasterService } from '../../../../core/services/toaster.service';
import { NormalDatePickerComponent } from '../../../../shared/components/simple-date-picker/normal-date-picker.component';
import { ToggleBtnsComponent } from '../../../../shared/components/toggle-btns/toggle-btns.component';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { AuditEntityType } from '../../../../core/models/audit-log.model';
import { AiHeadlinesComponent } from '../../../../shared/components/ai-headlines/ai-headlines.component';
import { BreakpointService } from '../../../../core/services/breakpoint.service';
import {
  MobilePlatformItem,
  SocialPlatformMobileComponent,
} from './mobile-view/social-platform-mobile.component';
import { AuditLogPanelComponent } from '../../../../shared/components/audit-log-panel/audit-log-panel.component';
import {
  DashboardWidgetPickerDialogComponent,
  type DashboardWidgetPickerDialogResult,
} from '../../../../shared/components/dashboard/dashboard-widget-picker-dialog.component';
import { DashboardSectionLayoutService } from '../../../../core/services/dashboard-section-layout.service';
import {
  DashboardPageKey,
  SocialPlatformSectionId,
  SocialPlatformSectionDef,
} from '../../../../core/enums/dashboard-cards.enum';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilter, findCatalogPageId, resolveActiveCatalogProductId, stripProductPrefix } from '../../../../shared/components/dashboard/dashboard-embed.helpers';


interface SocialPlatformStat {
  id: string;
  productId?: string;
  productName: string;
  platformName: string;
  date: string;
  views: number | string;
  impressions: number | string;
  follower: number | string;
  reach: number | string;
  watchTime: string;
  engagement: number | string;
  contentInteraction: number | string;
  subscribers: number | string;
  reachedAudience: number | string;
  uniqueViewers: number | string;
  follow: number | string;
}

interface ApiResponse {
  socialPlatforStats: SocialPlatformStat[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface FilterState {
  productId: string | null;
  platformId: string | null;
  dateRange: {
    start: string;
    end: string;
  } | null;
}

interface CardStatConfig {
  color: string;
  bg: string;
  icon: IconName;
  label: string;
  count: number;
}

/** Persisted layout state per section (visibility + width + order). */
interface SectionLayoutItem {
  id: SocialPlatformSectionId;
  visible: boolean;
  /** 4 | 6 | 8 | 12 — proportional width inside the 12-col dashboard grid. */
  colSpan: number;
  order: number;
}

/** Draft row used by the inline Customize Layout editor.
 *  Carries the section title alongside the persisted layout fields so the
 *  popup tile can render its label without re-looking-up `SECTIONS`. */
interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

import { LayoutEditorOverlayDirective } from '../../../../shared/components/dashboard/layout-editor-overlay.directive';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';

@Component({
  selector: 'app-social-platform',
  standalone: true,
  imports: [
    CommonModule,
    LayoutEditorOverlayDirective,
    SlidePanelComponent,
    ReactiveFormsModule,
    ButtonComponent,
    FormsModule,
    DropdownComponent,
    InputComponent,
    AlertComponent,
    IconComponent,
    DonutChartComponent,
    MultiHorizontalBarChartComponent,
    MultiLineChartComponent,
    IndividualToasterComponent,
    MatSlideToggleModule,
    WatchTimeChartComponent,
    NormalDatePickerComponent,
    ToggleBtnsComponent,
    AuditLogPanelComponent,
    AiHeadlinesComponent,
    SocialPlatformMobileComponent,
    PaginatedTableComponent,
    DragDropModule,
    IconComponentV2,
  ],
  templateUrl: './social-platform.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    ::ng-deep input::-webkit-outer-spin-button,
    ::ng-deep input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    ::ng-deep input[type=number] {
      -moz-appearance: textfield;
    }
    .card-bg {
      isolation: auto !important;
    }
  `],
})
export class SocialPlatformComponent implements OnInit {
  chartData: any = {};
  unit = signal<any>('month');
  apiservice: ApiService = inject(ApiService);
  toasterService: ToasterService = inject(ToasterService);
  private themeService = inject(ThemeService);
  public _breakpointService = inject(BreakpointService);
  isDarkTheme = signal(false);
  auditEntityTypes = [AuditEntityType.SOCIAL_PLATFORM_STATS];
  productPid = computed(() => {
    const id = this.selectedProduct();
    const products = this.apiservice.products();
    const product = products.find((p) => p.id === id);
    return product?.pid || null;
  });

  readonly HIDE_SNAPCHAT_FOR_COOL_CREDIT = true;

  isCoolCredit = computed(() => {
    const id = this.selectedProduct();
    const products = this.apiservice.products();
    const product = products.find((p) => p.id === id);
    return product?.name === 'Cool Credit';
  });

  isSnapchatHidden = computed(() => {
    return this.HIDE_SNAPCHAT_FOR_COOL_CREDIT && this.isCoolCredit();
  });

  filteredPlatforms = computed(() => {
    const platforms = this.platformsList();
    if (this.isSnapchatHidden()) {
      return platforms.filter((p) => p.label !== 'Snapchat');
    }
    return platforms;
  });

  // Data signals
  productsList = signal<DropdownOption[]>([]);
  platformsList = signal<DropdownOption[]>([]);
  statsData = signal<SocialPlatformStat[]>([]);
  filteredStatsData = signal<SocialPlatformStat[]>([]);
  isLoading = signal(false);
  isOverallLoading = signal(false);
  isDonutChartLoading = signal(false);
  isPlatformWiseLoading = signal(false);
  overallPerformanceStatsData = signal<any>([]);
  platformWisePerformanceStatsData = signal<any>([]);
  viewsAndFollowersStatsData = signal<any>([]);
  private dialog = inject(MatDialog);
  private breakpointObserver = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);
  readonly sectionLayout = inject(DashboardSectionLayoutService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);
  /**
   * Effective col-span the parent (e.g. My Dashboard unified grid) is
   * actually rendering this embedded widget at. When set in `mixedMode`,
   * we override each filtered section's `colSpan` so the inner conditional
   * grids (`sec.colSpan === 4` / `=== 6`) collapse to the compact layouts
   * that fit the tile's real on-screen width — instead of falling back to
   * the section's own standalone `layoutMap()` colSpan (usually `12`).
   */
  readonly embeddedColSpan = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);

  readonly PAGE_KEY = DashboardPageKey.SOCIAL_PLATFORM;
  readonly SectionId = SocialPlatformSectionId;
  readonly SECTIONS = SocialPlatformSectionDef;

  /**
   * Section ids that must NEVER appear in the Customize Layout editor or in
   * the drag-drop / Custom Dashboard flow. Audit Log is rendered as a
   * standalone block below the customizable grid, so it's filtered out of
   * `layoutMap`, `editorItems`, and the defaults reset.
   */
  private readonly LAYOUT_EXCLUDED_SECTION_IDS: ReadonlySet<SocialPlatformSectionId> =
    new Set<SocialPlatformSectionId>([SocialPlatformSectionId.AUDIT_LOG]);

  /** SECTIONS minus any section excluded from the customizable layout. */
  private readonly LAYOUT_SECTIONS = SocialPlatformSectionDef.filter(
    (s) => !this.LAYOUT_EXCLUDED_SECTION_IDS.has(s.id as SocialPlatformSectionId),
  );

  donutWidth = signal(600);
  donutHeight = signal(400);
  donutLegendPosition = signal<'right' | 'bottom'>('right');
  donutRadiusScale = signal(1);
  // Form and panel signals
  panelOpen = signal(false);
  form: FormGroup;
  formSubmitted = signal(false);
  errorMessage = signal<string>('');
  successMessage = signal<string>('');
  watchTimeMode = signal<boolean>(false);
  // ViewChild for date picker dropdown
  @ViewChild('dateDropdown') dateDropdown!: DropdownComponent;
  @ViewChild('platformDropdown') platformDropdown!: DropdownComponent;
  @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;

  // Filter state
  selectedProduct = signal<string | null>(null);
  /** Tags in-flight fetches so stale responses are ignored after product changes. */
  private dataFetchGeneration = 0;
  selectedPlatform = signal<string | null>(null);
  selectedPlatformId = signal<string | null>(null);
  selectedPlatformTypeNames = signal<string[]>([]);
  selectedPlatformName = signal<any>('');
  selectedDateRange = signal<{ start: string; end: string } | null>(null);
  selectedDateReference = signal<{ start: string; end: string } | null>(null);
  selectedDateReferenceYear = signal<{ start: string } | null>(null);
  private viewSignal: WritableSignal<'analyticsView' | 'tableView'> =
    signal('analyticsView');
  // API pagination (for server-side filtering)
  currentPage = signal(1);
  totalItems = signal(0);
  itemsPerPage = 10;

  // Track form values for computed signals
  formValues = signal<any>(null);

  isSubmitDisabled = computed(() => {
    const values = this.formValues();
    if (!values || this.form.invalid) return true;

    const dateValue = values.dateReference;
    if (!dateValue || !dateValue.year || !dateValue.month) return true;

    const selectedPlatform = this.selectedPlatformName();
    const config = this.platformFields[selectedPlatform] || [];

    // Check if any platform-specific field has a value (including 0)
    const hasAnyValue = config.some((field) => {
      const val = values[field.control];
      return val !== null && val !== '' && val !== undefined;
    });

    return !hasAnyValue;
  });

  // Date handling
  today: Date = new Date();
  todayYear: number = this.today.getFullYear();
  todayMonth: number = this.today.getMonth() + 1;

  readonly batchSize = 10;

  visibleCount = signal(this.batchSize);
  private userClearedSelection = signal(false);

  tableActionOptions = [
    {
      label: 'Edit',
      value: 'edit',
      iconName: 'squarePen' as IconName,
      isShow: true,
      color: '#1E6AFD',
    },
    {
      label: 'Delete',
      value: 'delete',
      iconName: 'trash' as IconName,
      isShow: true,
      color: 'red',
    },
  ];

  tabOptions = [
    {
      label: 'Analytics View',
      tooltip: 'Click to View Analytics View',
      isTable: false,
      value: 'analyticsView',
      iconName: 'chartLine' as IconName,
    },
    {
      label: 'Table View',
      tooltip: 'Click to Table View',
      isTable: true,
      value: 'tableView',
      iconName: 'betweenHorizontalStart' as IconName,
    },
  ];

  viewOptions: DropdownOption[] = [
    {
      label: 'Analytics View',
      value: 'analyticsView',
      icon: 'chartLine',
    },
    {
      label: 'Table View',
      value: 'tableView',
      icon: 'betweenHorizontalStart',
    },
  ];

  get view() {
    return this.viewSignal();
  }

  setActiveTab(tab: any): void {
    this.viewSignal.set(tab);
  }

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      product: [''],
      platform: [''],
      dateReference: [''],
      followersCount: [null, [Validators.min(0), Validators.max(2000000000), Validators.pattern('^[0-9]*$')]],
      viewCount: [null, [Validators.min(0), Validators.max(2000000000), Validators.pattern('^[0-9]*$')]],
      impressionsCount: [null, [Validators.min(0), Validators.max(2000000000), Validators.pattern('^[0-9]*$')]],
      watchTime: [null, [Validators.min(0), Validators.max(2000000000), this.watchTimeValidator.bind(this)]],
      reachCount: [null, [Validators.min(0), Validators.max(2000000000), Validators.pattern('^[0-9]*$')]],
      engagementCount: [null, [Validators.min(0), Validators.max(2000000000), Validators.pattern('^[0-9]*$')]],
      contentInteractionCount: [null, [Validators.min(0), Validators.max(2000000000), Validators.pattern('^[0-9]*$')]],
      subscribersCount: [null, [Validators.min(0), Validators.max(2000000000), Validators.pattern('^[0-9]*$')]],
      reachAudienceCount: [null, [Validators.min(0), Validators.max(2000000000), Validators.pattern('^[0-9]*$')]],
      uniqueViewers: [null, [Validators.min(0), Validators.max(2000000000), Validators.pattern('^[0-9]*$')]],
      followCount: [null, [Validators.min(0), Validators.max(2000000000), Validators.pattern('^[0-9]*$')]],
    });

    // Sync form values to signal
    this.form.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((values) => {
        this.formValues.set(values);
      });

    effect(() => {
      this.isDarkTheme.set(this.themeService.getTheme() === 'dark');

      // const statsMap = this.platformChartStatsMap();
      // if (!statsMap) return;

      // const availablePlatforms = Array.from(statsMap.keys()).filter(
      //   (platform) => this.hasPlatformData(platform)
      // );

      // this.selectedPlatformTypeNames.update((selected) => {
      //   // 1️⃣ NO DATA → clear selection
      //   if (availablePlatforms.length === 0) {
      //     return [];
      //   }

      //   // 2️⃣ Remove platforms that lost data
      //   const stillValid = selected.filter((p) =>
      //     availablePlatforms.includes(p)
      //   );

      //   // 3️⃣ DATA EXISTS but NOTHING selected → auto-select ONE
      //   if (stillValid.length === 0) {
      //     return availablePlatforms.slice(0, 1);
      //   }

      //   // 4️⃣ Preserve user selection
      //   return stillValid;
      // });

      const statsMap = this.platformChartStatsMap();
      if (!statsMap) return;

      const availablePlatforms = Array.from(statsMap.keys()).filter(
        (platform) => this.hasPlatformData(platform)
      );

      this.selectedPlatformTypeNames.update((selected) => {
        // 1️⃣ No data → clear everything & reset flag
        if (availablePlatforms.length === 0) {
          this.userClearedSelection.set(false);
          return [];
        }

        // 2️⃣ Remove invalid selections
        const stillValid = selected.filter((p) =>
          availablePlatforms.includes(p)
        );

        // 3️⃣ Auto-select ONLY when:
        //    - nothing selected
        //    - AND user did NOT clear manually
        if (stillValid.length === 0 && !this.userClearedSelection()) {
          return availablePlatforms.slice(0, 1);
        }

        // 4️⃣ Respect user selection
        return stillValid;
      });
    });

    effect(() => {
      const productId =
        this.apiservice.getSelectedProductId() ||
        sessionStorage.getItem('selected_product_id');

      if (productId && productId !== this.selectedProduct()) {
        untracked(() => {
          this.dataFetchGeneration += 1;
          this.selectedProduct.set(productId);
        });
      }
    });

    // Effect for stats that only depend on Product (Overall Performance, Views/Followers)
    effect(() => {
      const productId = this.selectedProduct();
      if (!productId) return;

      untracked(() => {
        this.overallPerformanceStats();
        this.viewsAndFollowersStats();
      });
    });

    // Effect for platform-wise stats (depends on Product, Unit, DateRange)
    effect(() => {
      const productId = this.selectedProduct();
      const dateRange = this.selectedDateRange();
      const unit = this.unit();

      // WAIT for both product and date range before calling
      if (!productId || !dateRange) return;

      untracked(() => {
        this.platformWisePerformanceStats();
      });
    });

    // In embedded ("mixedMode") mode the date-picker chrome is hidden, so
    // `selectedDateRange` would otherwise stay `null` forever and the
    // platform-wise stats API above would never fire. That left the
    // "Views Trend Across Platforms" chart empty AND every platform pill
    // disabled (because `hasPlatformData()` reads from the same data
    // source) — i.e. only Instagram (the default selection) showed a
    // flat zero line and no other pill was clickable.
    //
    // Seed the same "Last 3 Months" range the standalone date-picker
    // initializes with (`selectedStartRange` / `selectedEndRange`) so the
    // chart loads real data and the pills enable themselves.
    effect(() => {
      if (!this.mixedMode()) return;
      const productId = this.selectedProduct();
      if (!productId) return;
      if (this.selectedDateRange() != null) return;
      untracked(() => {
        const start = dayjs().tz(LA_TIMEZONE).subtract(89, 'day').format('YYYY-MM');
        const end = dayjs().tz(LA_TIMEZONE).format('YYYY-MM');
        this.unit.set('month');
        this.selectedDateRange.set({ start, end });
      });
    });

    // Effect for table stats (depends on Product, Platform, DateRef, DateYear)
    effect(() => {
      const productId = this.selectedProduct();
      const platformId = this.selectedPlatformId();
      const dateRef = this.selectedDateReference();
      const dateYear = this.selectedDateReferenceYear();

      if (!productId) return;

      untracked(() => {
        this.loadStats();
      });
    });

    this.breakpointObserver
      .observe(['(max-width: 1536px)', '(max-height: 620px)'])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        const isSmallWidth = result.breakpoints['(max-width: 1536px)'];
        const isSmallHeight = result.breakpoints['(max-height: 620px)'];

        if (isSmallWidth || isSmallHeight) {
          this.donutWidth.set(400);
          this.donutHeight.set(250);
          this.donutLegendPosition.set('right');
          this.donutRadiusScale.set(0.65);
        } else {
          this.donutWidth.set(600);
          this.donutHeight.set(400);
          this.donutLegendPosition.set('right');
          this.donutRadiusScale.set(1);
        }
      });

    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiservice.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiservice);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<SocialPlatformSectionId>();
          keys.forEach((k) => {
            if (Object.values(SocialPlatformSectionId).includes(k as SocialPlatformSectionId)) {
              pinned.add(k as SocialPlatformSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    // Pre-resolve catalog `widgets.id` per section so per-click adds can
    // skip the catalog round-trip (matches MR1's pattern of carrying
    // `widgetId` on each layout item) AND so the template can hide the
    // pin button for sections with no backend counterpart (today:
    // `sp_audit_log` everywhere, and `sp_overal_analytics` on every
    // product except Denefits — Denefits ships `de_sp_overal_analytics`,
    // others will start resolving once backend adds their per-product rows).
    //
    // Sections in `NEVER_PINNABLE_SECTIONS` (Audit Log) are filtered out
    // here too so they cannot be sent to the dashboards API even if the
    // backend ever starts advertising them in the catalog.
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiservice);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<SocialPlatformSectionId, number>();
            for (const sec of Object.values(SocialPlatformSectionId)) {
              if (this.NEVER_PINNABLE_SECTIONS.has(sec)) continue;
              const id = widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });
  }

  ngOnInit() {
    // Hydrate visibility / colSpan / order from localStorage. This replaces
    // the old `sectionLayout.init` call — we now manage layout locally so the
    // page can also support per-card resize + drag-drop reordering.
    this._initLayout();
    // this.loadProducts();
    this.loadPlatforms();
    const productId = sessionStorage.getItem('selected_product_id');
    // this.loadStats();
    if (productId) {
      this.selectedProduct.set(productId);
      // this.overallPerformanceStats();
      // this.platformWisePerformanceStats();
    }
  }

  // ─── Section layout state (visibility + colSpan + order) ──────────────
  /** Persisted layout map driving the actual rendered page. */
  readonly layoutMap = signal<Record<SocialPlatformSectionId, SectionLayoutItem>>(
    {} as Record<SocialPlatformSectionId, SectionLayoutItem>,
  );

  /** All sections sorted by their persisted order. */
  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  /** Sections currently visible, in order — used by the analytics-view grid. */
  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  private readonly _embeddedVisibleSectionsBase = createEmbeddedSectionFilter(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.orderedSections,
  );

  /**
   * Sections shown by the analytics-view grid.
   *
   * Standalone: same as `orderedVisibleSections` (no override).
   * Embedded (`mixedMode`): when the parent dashboard passes
   * `embeddedColSpan` (the tile's actual rendered width on My Dashboard),
   * we clone the filtered sections with `colSpan` set to that override so
   * the inner `sec.colSpan === 4` / `=== 6` template branches pick the
   * compact layouts that match the tile's real width. Without the
   * override (or when not in `mixedMode`), behavior is unchanged.
   */
  readonly embeddedVisibleSections = computed<SectionLayoutItem[]>(() => {
    const base = this._embeddedVisibleSectionsBase();
    if (!this.mixedMode()) return base;
    const override = this.embeddedColSpan();
    if (override == null) return base;
    const normalized = this._normalizeColSpan(override);
    return base.map((s) =>
      s.colSpan === normalized ? s : { ...s, colSpan: normalized },
    );
  });

  /**
   * Sections currently rendered by the host. On the standalone page
   * (`mixedMode() === false`) every section is considered active so all
   * loaders run as before. When mounted inside `WidgetHostComponent`
   * (My Dashboard), only the sections matching `visibleWidgetKeys` are
   * active — that's the set the data loaders below gate on.
   *
   * IMPORTANT: derived directly from `visibleWidgetKeys()` (synchronous
   * host input), NOT from `embeddedVisibleSections()` — the latter
   * depends on `orderedVisibleSections` which is empty until the layout
   * is hydrated from localStorage, and would cause every guarded loader
   * to short-circuit on first paint.
   */
  private readonly activeSectionIds = computed<ReadonlySet<string>>(() => {
    const keys = this.visibleWidgetKeys();
    if (!keys || keys.length === 0) return new Set();
    const allowed = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      allowed.add(k);
      const stripped = stripProductPrefix(k);
      if (stripped !== k) allowed.add(stripped);
    }
    return allowed;
  });

  /**
   * Returns true if any of the given sections is currently rendered by
   * the host (always true on the standalone page). Used as a one-line
   * guard at the top of each loader so embedded "single card" mounts
   * skip the data requests they don't need.
   */
  private isSectionActive(...sectionIds: SocialPlatformSectionId[]): boolean {
    if (!this.mixedMode()) return true;
    const active = this.activeSectionIds();
    // Defensive: when host did not send any keys (or none resolved), fall
    // back to "active" so we never accidentally suppress a legitimate
    // request.
    if (active.size === 0) return true;
    return sectionIds.some((id) => active.has(id));
  }

  isVisible(sectionId: SocialPlatformSectionId): boolean {
    return this.layoutMap()[sectionId]?.visible ?? true;
  }

  /** Tailwind col-span class for a section in the live 12-col grid. */
  getSectionColClass(item: SectionLayoutItem | SocialPlatformSectionId): string {
    const id = typeof item === 'string' ? item : item.id;
    const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
    switch (span) {
      case 4: return 'col-span-12 lg:col-span-4';
      case 6: return 'col-span-12 lg:col-span-6';
      case 8: return 'col-span-12 lg:col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

  // ─── "Pin to Custom Dashboard" (API-backed) ─────────────────────────────
  readonly pinnedSections = signal<Set<SocialPlatformSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<SocialPlatformSectionId>>(new Set());

  /**
   * Sections that are explicitly excluded from the Custom ("My") Dashboard
   * by product rule, regardless of whether the backend catalog advertises
   * them. The audit trail is a page-local diagnostic surface — it doesn't
   * belong on the user's personalized dashboard, so we never offer the
   * pin button for it (and never seed it into the mixed dashboard).
   *
   * This is a defensive denylist that runs IN ADDITION TO the catalog
   * gate: if a backend release ever starts advertising `sp_audit_log` in
   * the catalog, the section will still be unpinnable on the frontend.
   */
  private readonly NEVER_PINNABLE_SECTIONS: ReadonlySet<SocialPlatformSectionId> =
    new Set<SocialPlatformSectionId>([SocialPlatformSectionId.AUDIT_LOG]);

  /**
   * Catalog `widgets.id` resolved per section, keyed by canonical
   * `SocialPlatformSectionId`. Populated once in the constructor effect
   * after the catalog products + page id resolve.
   *
   * Sections NOT present in this map have no backend catalog entry — the
   * template uses {@link canPinToCustomDashboard} to hide the pin button
   * for those (avoids the "Widget X is not registered" toast). Sections
   * that ARE in the map use the pre-resolved numeric `widgetId` with
   * {@link CustomDashboardPinService.toggleLayoutItem}, mirroring the
   * Monthly Report 1 / 2 pattern (one click = one widget on the API,
   * never the whole page).
   *
   * Sections in {@link NEVER_PINNABLE_SECTIONS} are stripped before the
   * map is set, so they never become pinnable even if the backend
   * catalog starts advertising them.
   */
  readonly pinnableWidgetIds = signal<Map<SocialPlatformSectionId, number>>(
    new Map(),
  );

  /**
   * Resolves the section id from either a layout item (template loop) or a
   * raw enum value (TS callers). The template iterates `SectionLayoutItem`
   * objects via `embeddedVisibleSections()`, so the pin handlers must
   * accept that shape.
   */
  private _toSectionId(
    sec: SectionLayoutItem | SocialPlatformSectionId,
  ): SocialPlatformSectionId {
    return typeof sec === 'string' ? sec : sec.id;
  }

  isAddedToCustomDashboard(
    sec: SectionLayoutItem | SocialPlatformSectionId,
  ): boolean {
    return this.pinnedSections().has(this._toSectionId(sec));
  }

  isAddingToCustomDashboard(
    sec: SectionLayoutItem | SocialPlatformSectionId,
  ): boolean {
    return this.pendingPinSections().has(this._toSectionId(sec));
  }

  /**
   * Returns `true` when the given section is allowed AND has a catalog
   * `widgets.id` we can send to the dashboards API.
   *
   * Two gates run in order:
   *   1. {@link NEVER_PINNABLE_SECTIONS} — product-level denylist (e.g.
   *      Audit Log). These ALWAYS return `false`, regardless of catalog
   *      state.
   *   2. {@link pinnableWidgetIds} — backend catalog presence. Frontend-
   *      only sections (e.g. `sp_audit_log`, or `sp_overal_analytics` on
   *      products that don't yet ship that row) and sections still
   *      loading return `false` here so the template hides the pin
   *      button instead of letting the user click a known-failing
   *      action.
   */
  canPinToCustomDashboard(
    sec: SectionLayoutItem | SocialPlatformSectionId,
  ): boolean {
    const sectionId = this._toSectionId(sec);
    if (this.NEVER_PINNABLE_SECTIONS.has(sectionId)) return false;
    return this.pinnableWidgetIds().has(sectionId);
  }

  /**
   * Quick-action toggle handler. Mirrors `MonthlyReport1Component.addToCustomDashboard`:
   *   • Pre-resolved numeric `widgetId` (from {@link pinnableWidgetIds})
   *     is forwarded into {@link CustomDashboardPinService.toggleLayoutItem}.
   *   • The service performs the existence probe + `PATCH /visibility` or
   *     seed `POST /dashboards`. Only ONE widget id is sent — the
   *     dashboard never receives the whole page on a single click.
   */
  addToCustomDashboard(
    sec: SectionLayoutItem | SocialPlatformSectionId,
  ): void {
    const sectionId = this._toSectionId(sec);
    if (!sectionId) return;
    // Defense in depth — Audit Log (and any other denylisted section) is
    // never allowed on the Custom Dashboard, even if a future template
    // change exposes the button or the backend catalog starts shipping
    // a widget id for it.
    if (this.NEVER_PINNABLE_SECTIONS.has(sectionId)) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) {
      this.toasterService.error(
        'Custom Dashboard is not ready yet. Please try again in a moment.',
      );
      return;
    }

    const widgetId = this.pinnableWidgetIds().get(sectionId);
    if (widgetId == null) {
      // Frontend-only section (no backend widget). The template's
      // `canPinToCustomDashboard` gate hides the button for these, so
      // landing here means the catalog hasn't loaded yet.
      return;
    }

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;

    this.customDashboardPin.toggleLayoutItem({
      pageId,
      widgetId,
      sourcePageId: pageId,
      colSpan,
      rowSpan: 1,
      customConfig: {},
      isCurrentlyPinned: this.isAddedToCustomDashboard(sectionId),
      onPinnedChange: (pinned) => {
        this.pinnedSections.update((s) => {
          const next = new Set(s);
          if (pinned) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
      },
      onPendingChange: (pending) => {
        this.pendingPinSections.update((s) => {
          const next = new Set(s);
          if (pending) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
      },
    });
  }

  // ─── Customize Layout editor (mini-dashboard preview) ──────────────────
  /** State for the inline Customize Layout editor. */
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);

  /** Number of cards currently marked visible in the editor draft. */
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /**
   * localStorage key for this page's persisted layout.
   *
   * v2 bump: section ids were migrated from product-prefixed
   * (`de_sp_overall_stats`, etc.) to canonical (`sp_overall_stats`) so the
   * page resolves catalog widgets across every product. Bumping the key
   * forces a one-time graceful fall-back to defaults for users whose
   * v1 payload is keyed by the old `de_sp_*` ids.
   */
  private readonly LAYOUT_LS_KEY = 'prism_social_platform_layout_v2';

  /**
   * Default col-span per section — matches the historical full-width layout.
   * Only includes sections that participate in the customizable grid; AUDIT_LOG
   * is rendered as a standalone block and is intentionally excluded.
   */
  private readonly DEFAULT_COL_SPAN: Partial<Record<SocialPlatformSectionId, number>> = {
    [SocialPlatformSectionId.OVERALL_STATS]:        12,
    [SocialPlatformSectionId.PLATFORM_PERFORMANCE]: 12,
    [SocialPlatformSectionId.VIEWS_TREND]:          12,
    [SocialPlatformSectionId.OVERALL_ANALYTICS]:    12,
    // Mirror backend catalog `default_cols: 6` for the split-out
    // standalone variants of the Overall Analytics sub-charts.
    [SocialPlatformSectionId.WATCH_TIME]:           6,
    [SocialPlatformSectionId.FOLLOW_BY_PLATFORM]:   6,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: LayoutEditorItem[] = this.orderedSections()
      .filter((s) => !this.LAYOUT_EXCLUDED_SECTION_IDS.has(s.id))
      .map((s) => ({
        id: s.id,
        title: titleById.get(s.id) ?? s.id,
        visible: s.visible,
        colSpan: this._normalizeColSpan(s.colSpan),
        order: s.order,
      }));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: SocialPlatformSectionId): void {
    // When a card is hidden, push it to the bottom so the visible cards
    // reflow into the freed grid slot. When shown again, slot it back in
    // just above the first hidden item so it rejoins the visible group.
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
  }

  /** Restores the editor draft to the page's default order, widths, and visibility. */
  resetLayoutEditorToDefaults(): void {
    const defaults: LayoutEditorItem[] = this.LAYOUT_SECTIONS.map((s, idx) => ({
      id: s.id as SocialPlatformSectionId,
      title: s.title,
      visible: true,
      colSpan: this._normalizeColSpan(
        this.DEFAULT_COL_SPAN[s.id as SocialPlatformSectionId] ?? 12,
      ),
      order: idx,
    }));
    this.editorItems.set(defaults);
  }

  /**
   * Persists the editor draft to local layout state + localStorage,
   * then writes through to the user's mixed/Custom dashboard via the
   * shared `CustomizeLayoutBatchService` so the layout survives a
   * refresh and stays in sync with what the dashboards-API knows.
   *
   * Backend write is best-effort: when `pageId`/widgets aren't
   * resolved yet (catalog still loading, or page has no backend
   * counterpart) or when the user has no mixed dashboard yet, the
   * service silently no-ops — local state + localStorage still
   * apply, exactly like before.
   */
  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout: Record<SocialPlatformSectionId, SectionLayoutItem> =
      {} as Record<SocialPlatformSectionId, SectionLayoutItem>;
    draft.forEach((d, idx) => {
      layout[d.id] = {
        id: d.id,
        visible: d.visible,
        colSpan: this._normalizeColSpan(d.colSpan),
        order: idx,
      };
    });
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);

    // Best-effort backend persistence. Maps each draft row to its
    // pre-resolved catalog `widgets.id`; rows whose section has no
    // backend widget (e.g. Audit Log) are dropped so they never
    // leak into the batch payload.
    const pageId = this.effectiveCatalogPageId();
    const widgetIdMap = this.pinnableWidgetIds();
    if (pageId != null && widgetIdMap.size > 0) {
      const batchDraft = draft
        .filter((d) => widgetIdMap.has(d.id))
        .map((d) => ({
          widgetId: widgetIdMap.get(d.id) ?? 0,
          sourcePageId: pageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: d.order,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId,
          productId: resolveActiveCatalogProductId(this.apiservice),
          fallbackSourcePageId: pageId,
          draft: batchDraft,
        });
      }
    }

    this.isLayoutEditorOpen.set(false);
  }

  // ─── Editor: width chips (decrease / increase / labels) ─────────────────
  /** Steps a card down one width. Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: SocialPlatformSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
  }

  increaseEditorCardColSpan(id: SocialPlatformSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width).
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
  }

  /** Whether clicking Auto Adjust Width would actually grow at least one card. */
  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(this.editorItems()).size > 0;
  }

  /**
   * Whether the minimize button should be active for this card. A card
   * cannot shrink below its designed default col-span — e.g. a card
   * designed for `col-8` can only step between 8 and 12.
   */
  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether `span` is below the designed default col-span for `id`. */
  private _isBelowDefaultColSpan(
    id: SocialPlatformSectionId,
    span: number,
  ): boolean {
    const defaultSpan = this.DEFAULT_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  /** Tailwind col-span class for an editor tile inside the popup's 12-col grid. */
  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4:  return 'col-span-4';
      case 6:  return 'col-span-6';
      case 8:  return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  /** Human-friendly label for a col-span value (Small / Medium / Large / Full). */
  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4:  return 'Small';
      case 6:  return 'Medium';
      case 8:  return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }
/* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
  const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
    strategy: 'pairwise-swap',
  });
  if (next) this.editorItems.set(next);
}
 

  // ─── Layout state private helpers ──────────────────────────────────────
  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<SocialPlatformSectionId, SectionLayoutItem> =
      {} as Record<SocialPlatformSectionId, SectionLayoutItem>;

    // Iterate only the sections that participate in the customizable grid.
    // AUDIT_LOG is rendered as a standalone block outside the drag-drop layout
    // and must not appear in `layoutMap` (otherwise it would be picked up by
    // the @for loop in the analytics view template).
    this.LAYOUT_SECTIONS.forEach((s, idx) => {
      const sid = s.id as SocialPlatformSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    layout: Record<SocialPlatformSectionId, SectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable (e.g. private mode) — silently ignore.
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  selectedRowItem(item: any) {
    if (!item || !item.row) return;

    const row = item.row;
    if (item.action === 'edit') {
      this.editStats(row);
    } else if (row?.id) {
      // this.handleRemoveMasterPromptList(row?.id)
      this.openConfirmationDialog(row);
    }
  }

  openConfirmationDialog(row: any) {
    this.dialog
      .open(ConfirmationModalComponent, {
        width: '400px',
        data: {
          title: 'Confirm Deletion',
          description: `Are you sure you want to delete this ${row?.productName} item?`,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.handleRemoveMasterPromptList(row?.id);
        }
      });
  }

  handleRemoveMasterPromptList(id: any) {
    try {
      this.apiservice.delete(`/social-platform/delete-stat/${id}`).subscribe({
        next: (response) => {
          this.toasterService.success(
            'Item removed successfully',
            'Stats',
            3000
          );
          // this.loadMasterPrompts();
          this.loadStats();
          this.auditLogPanel?.reloadLogs();
        },
        error: (error: any) => {
          this.errorMessage.set(
            error?.error || error?.message || 'Error removing stats'
          );
        },
      });
    } catch (error) {
      console.error('Delete category error:', error);
    }
  }

  isInvalid(
    controlName: string,
    hardCodeFormSubmitted: boolean = false
  ): boolean {
    const control = this.form.get(controlName);

    let condition =
      control && control.invalid && (control.touched || control.dirty);

    if (hardCodeFormSubmitted) {
      condition = condition && this.formSubmitted();
    } else {
      condition = condition || this.formSubmitted();
    }

    return !!condition;
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = this.form.get(controlName);

    if (
      !control ||
      (control.valid && !control.touched && !this.formSubmitted())
    ) {
      return undefined;
    }

    if (
      control.invalid &&
      (control.touched || control.dirty || this.formSubmitted())
    ) {
      if (control.hasError('required')) {
        if (['product', 'platform'].includes(controlName)) {
          return 'Please select an option';
        }
        if (controlName === 'dateReference') {
          return 'Please select a date';
        }
        return 'This field is required';
      }

      if (control.hasError('min')) {
        const requiredMin = control.errors?.['min']?.min;
        return `Value must be ${requiredMin} or greater`;
      }

      if (control.hasError('max')) {
        const requiredMax = control.errors?.['max']?.max;
        return `Value must be ${requiredMax.toLocaleString()} or less`;
      }

      if (control.hasError('pattern')) {
        return 'Please enter only numbers';
      }

      if (control.hasError('invalidWatchTime')) {
        if (this.watchTimeMode()) {
          return 'Enter as H.MM (e.g. 1.30, minutes 00-59)';
        }
        return 'Please enter minutes as a whole number';
      }
    }

    return undefined;
  }

  loadProducts(): void {
    if (this.productsList().length === 0) {
      this.apiservice
        .get(
          '/product/list-product',
          {},
          {
            showErrorToaster: true,
            showSuccessToaster: false,
            showLoader: false,
            cache: true,
          }
        )
        .subscribe({
          next: (response: any) => {
            const filteredProducts = response
              .filter((item: any) => item.active)
              .map((item: any) => ({
                value: item.id,
                label: item.name,
              }));

            this.productsList.set(filteredProducts);
            const productId = sessionStorage.getItem('selected_product_id');
            this.selectedProduct.set(productId);
            // this.overallPerformanceStats();
            // this.platformWisePerformanceStats();
            // this.viewsAndFollowersStats();
          },
          error: (error: any) =>
            console.error('Error fetching products:', error),
        });
    }
  }

  loadPlatforms(): void {
    // Platform list feeds (a) the Add/Edit form's platform dropdown
    // (only shown standalone) and (b) the platform-pill row inside the
    // VIEWS_TREND chart. When embedded as a single card that isn't
    // VIEWS_TREND there is nothing to populate, so skip the call.
    if (
      this.mixedMode() &&
      !this.isSectionActive(SocialPlatformSectionId.VIEWS_TREND) &&
      !this.isSectionActive(SocialPlatformSectionId.PLATFORM_PERFORMANCE)
    ) {
      return;
    }

    if (this.platformsList().length === 0) {
      this.apiservice
        .get(
          '/social-platform/list-social-platform',
          {},
          {
            showErrorToaster: true,
            showSuccessToaster: false,
            showLoader: false,
            cache: true,
          }
        )
        .subscribe({
          next: (response: any) => {
            const platforms = response.map((item: any) => ({
              value: item.id,
              label: item.name,
            }));

            this.platformsList.set(platforms);
            this.selectedPlatformTypeNames.set(
              platforms[0] ? [platforms[0].label] : []
            );
          },
          error: (error: any) =>
            console.error('Error fetching platforms:', error),
        });
    }
  }

  loadStats(): void {
    // The paginated stats table only renders in the standalone Table View
    // (`@if (view == 'tableView')` in the template). The table view is
    // never shown when the report is embedded inside `WidgetHostComponent`
    // (My Dashboard / mixed mode), so the request can be skipped entirely.
    if (this.mixedMode()) return;

    this.isLoading.set(true);
    const fetchGeneration = this.dataFetchGeneration;
    const fetchProductId = this.selectedProduct();
    const payload = {
      page: 1,
      limit: 100, // Get more data for client-side pagination
      productId: this.selectedProduct(),
      platformId: this.selectedPlatformId(),
      date: this.getDateRangeForApi() || null,
      year: this.selectedDateReferenceYear()?.start || null,
    };

    this.apiservice
      .get('/social-platform/list-social-stats', payload, {
        showLoader: false,
        showErrorToaster: true,
        showSuccessToaster: false,
      })
      .subscribe({
        next: (response: any) => {
          const isStale = fetchGeneration !== this.dataFetchGeneration
            || fetchProductId !== this.selectedProduct();
          if (isStale) {
            this.isLoading.set(false);
            return;
          }
          const apiResponse = response as ApiResponse;
          // prism-new rows carry productId only; the table and edit form read productName.
          const stats = (apiResponse.socialPlatforStats || []).map((s: any) =>
            s.productName ? s : { ...s, productName: this.productsList().find((d: DropdownOption) => d.value === s.productId)?.label ?? s.productName },
          );
          const filtered = this.applyClientSideTableFilters(stats);
          this.statsData.set(stats);
          this.filteredStatsData.set(filtered);
          this.totalItems.set(
            this.hasActiveTableFilters()
              ? filtered.length
              : apiResponse.pagination?.total || filtered.length
          );
          this.isLoading.set(false);
        },
        error: (error: any) => {
          console.error('Error fetching stats:', error);
          this.isLoading.set(false);
          this.errorMessage.set('Failed to load social platform statistics');
        },
      });
  }

  private getDateRangeForApi(): string | null {
    return this.getSelectedTableMonthKey();
  }

  private getSelectedTableMonthKey(): string | null {
    const dateRange = this.selectedDateReference();
    if (!dateRange?.start) return null;

    const start = dateRange.start;
    // Month picker stores calendar dates as YYYY-MM-DD; use literal month to avoid TZ shift.
    if (/^\d{4}-\d{2}-\d{2}$/.test(start)) {
      return start.slice(0, 7);
    }

    return dayjs.utc(start).tz(LA_TIMEZONE).format('YYYY-MM');
  }

  private getStatMonthKey(date: string | null | undefined): string | null {
    if (!date) return null;

    if (/^\d{4}-\d{2}$/.test(date)) {
      return date;
    }

    if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
      return date.slice(0, 7);
    }

    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.format('YYYY-MM') : null;
  }

  private hasActiveTableFilters(): boolean {
    return !!(
      this.selectedPlatformId() ||
      this.getSelectedTableMonthKey() ||
      this.selectedDateReferenceYear()?.start
    );
  }

  private applyClientSideTableFilters(
    stats: SocialPlatformStat[]
  ): SocialPlatformStat[] {
    let result = stats;

    const platformId = this.selectedPlatformId();
    if (platformId) {
      const platform = this.platformsList().find((p) => p.value === platformId);
      if (platform) {
        result = result.filter((stat) => stat.platformName === platform.label);
      }
    }

    const monthFilter = this.getSelectedTableMonthKey();
    if (monthFilter) {
      result = result.filter(
        (stat) => this.getStatMonthKey(stat.date) === monthFilter
      );
      return result;
    }

    const yearFilter = this.selectedDateReferenceYear()?.start;
    if (yearFilter) {
      const year = String(yearFilter);
      result = result.filter((stat) =>
        this.getStatMonthKey(stat.date)?.startsWith(year)
      );
    }

    return result;
  }

  onProductFilter(productId: string | string[] | null): void {
    const selectedId = Array.isArray(productId) ? productId[0] : productId;
    this.selectedProduct.set(selectedId === '' ? null : selectedId);
  }

  onPlatformFilter(platformId: string | string[] | null): void {
    const selectedId = Array.isArray(platformId) ? platformId[0] : platformId;
    this.selectedPlatformId.set(selectedId === '' ? null : selectedId);
  }

  onDateRangeFilter(dateValue: any): void {
    const { start_date, end_date, unit } = dateValue;
    this.unit.set(unit || 'month');

    // this.unit.set(unit);
    const finalStartDate =
      start_date?.length === 7
        ? start_date
        : dayjs.utc(start_date).tz(LA_TIMEZONE).format('YYYY-MM');
    const finalEndDate =
      end_date?.length === 7
        ? end_date
        : dayjs.utc(end_date).tz(LA_TIMEZONE).format('YYYY-MM');
    // const start = dayjs(start_date);
    // const end = dayjs(end_date);

    // // precise difference in years
    // const diffInYears = end.diff(start, 'year', true);

    // if (diffInYears <= 1) {
    //   this.unit.set('month');
    // } else if (diffInYears < 2) {
    //   this.unit.set('quarter');
    // } else {
    //   this.unit.set('year');
    // }

    // if (unit == 'week' || unit == 'day') {
    //   this.unit.set('month');
    // } else {

    // }

    if (finalStartDate && finalEndDate) {
      this.selectedDateRange.set({ start: finalStartDate, end: finalEndDate });
    } else {
      this.selectedDateRange.set(null);
    }
  }

  onDateFilter(dateValue: any): void {
    const closeDateDropdown = () => {
      setTimeout(() => {
        if (this.dateDropdown) {
          this.dateDropdown.close();
        }
      }, 100);
    };

    if (dateValue?.year && dateValue?.month) {
      const month = String(dateValue.month).padStart(2, '0');
      const monthStart = dayjs(`${dateValue.year}-${month}-01`);
      this.selectedDateReference.set({
        start: monthStart.format('YYYY-MM-DD'),
        end: monthStart.endOf('month').format('YYYY-MM-DD'),
      });
      this.selectedDateReferenceYear.set(null);
      closeDateDropdown();
      return;
    }

    if (dateValue?.year && !dateValue?.month) {
      this.selectedDateReferenceYear.set({ start: String(dateValue.year) });
      this.selectedDateReference.set(null);
      closeDateDropdown();
      return;
    }

    this.selectedDateReference.set(null);
    this.selectedDateReferenceYear.set(null);
  }

  clearAllFilters(): void {
    this.selectedPlatformId.set(null);
    this.selectedDateRange.set(null);
    this.selectedDateReference.set(null);
    this.selectedDateReferenceYear.set(null);

    if (this.platformDropdown) {
      this.platformDropdown.clearSelection();
    }
    if (this.dateDropdown) {
      this.dateDropdown.clearSelection();
    }

    this.loadStats();
  }

  formatValue(value: number | string): string {
    if (value === 'NA' || value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'string') {
      const num = parseFloat(value);
      if (isNaN(num)) return 'N/A';
      return num.toLocaleString();
    }
    return value.toLocaleString();
  }

  // Process Watch Time - input is in total Minutes from backend
  formatWatchTime(minutes: any): string {
    if (minutes === 'NA' || minutes === 'N/A' || minutes == null || minutes === undefined) return 'N/A';
    const mins = Number(minutes);
    if (isNaN(mins)) return 'N/A';

    return `${this.convertMinToHMM(mins)} hr`;
  }

  // Process data for paginated table - convert "NA" values to display format
  processedStatsData = computed(() => {
    const data = this.filteredStatsData().map((stat: any) => ({
      ...stat,
      views: stat.views === 'NA' ? 'N/A' : stat.views,
      impressions: stat.impressions === 'NA' ? 'N/A' : stat.impressions,
      follower: stat.follower === 'NA' ? 'N/A' : stat.follower,
      reach: stat.reach === 'NA' ? 'N/A' : stat.reach,
      watchTimeFormatted: stat.watchTime === 'NA' ? 'N/A' : this.formatWatchTime(stat.watchTime),
      engagement: stat.engagement === 'NA' ? 'N/A' : stat.engagement,
      contentInteraction:
        stat.contentInteraction === 'NA' ? 'N/A' : stat.contentInteraction,
      subscribers: stat.subscribers === 'NA' ? 'N/A' : stat.subscribers,
      reachedAudience:
        stat.reachedAudience === 'NA' ? 'N/A' : stat.reachedAudience,
      uniqueViewers: stat.uniqueViewers === 'NA' ? 'N/A' : stat.uniqueViewers,
      follow: stat.follow === 'NA' ? 'N/A' : stat.follow,
    }));

    if (this.isSnapchatHidden()) {
      return data.filter((stat: any) => stat.platformName !== 'Snapchat');
    }
    return data;
  });

  // Template helper methods
  Math = Math;

  productOptions = computed(() => {
    return [{ value: '', label: 'All Products' }, ...this.productsList()];
  });

  platformOptions = computed(() => {
    const platforms = this.isSnapchatHidden()
      ? this.platformsList().filter((p) => p.label !== 'Snapchat')
      : this.platformsList();
    return [{ value: '', label: 'All Platforms' }, ...platforms];
  });

  // Table configuration for paginated table
  tableHeaders: TableHeader[] = [
    { label: 'Product', key: 'productName', align: 'text-left' },
    { label: 'Platform', key: 'platformName', align: 'text-left' },
    {
      label: 'Date',
      key: 'date',
      align: 'text-center',
      dateFormat: 'monthYear',
    },
    { label: 'Views', key: 'views', align: 'text-left', transform: 'number' },
    {
      label: 'Impressions',
      key: 'impressions',
      align: 'text-left',
      transform: 'number',
    },
    {
      label: 'Followers',
      key: 'follower',
      align: 'text-left',
      transform: 'number',
    },
    { label: 'Reach', key: 'reach', align: 'text-left', transform: 'number' },
    { label: 'Watch Time', key: 'watchTimeFormatted', align: 'text-left' },
    {
      label: 'Engagement',
      key: 'engagement',
      align: 'text-left',
      transform: 'number',
    },
    {
      label: 'Content Interaction',
      key: 'contentInteraction',
      align: 'text-left',
      transform: 'number',
    },
    {
      label: 'Subscribers',
      key: 'subscribers',
      align: 'text-left',
      transform: 'number',
    },
    {
      label: 'Reached Audience',
      key: 'reachedAudience',
      align: 'text-left',
      transform: 'number',
    },
    {
      label: 'Unique Viewers',
      key: 'uniqueViewers',
      align: 'text-left',
      transform: 'number',
    },
    {
      label: 'Follows',
      key: 'follow',
      align: 'text-left',
      transform: 'number',
    },
  ];

  openPanel() {
    if (!this.isEdit()) {
      const platforms = this.filteredPlatforms();
      this.selectedPlatform.set(platforms[0]?.value);
      this.selectedPlatformName.set(platforms[0]?.label);
      this.applyPlatformFieldVisibility(platforms[0]?.label);
    }
    this.panelOpen.set(true);
    this.formValues.set(this.form.value);
  }

  closePanel() {
    this.panelOpen.set(false);
    this.form.reset({
      followersCount: null,
      viewCount: null,
      impressionsCount: null,
      watchTime: null,
      reachCount: null,
      engagementCount: null,
      contentInteractionCount: null,
      subscribersCount: null,
      reachAudienceCount: null,
      uniqueViewers: null,
      followCount: null,
      product: '',
      platform: '',
      dateReference: '',
    });
    this.formSubmitted.set(false);
  }

  apiKeyMap: Record<string, string> = {
    followersCount: 'follower',
    viewCount: 'views',
    impressionsCount: 'impressions',
    watchTime: 'watchTime',
    reachCount: 'reach',
    engagementCount: 'engagement',
    contentInteractionCount: 'contentInteraction',
    subscribersCount: 'subscribers',
    reachAudienceCount: 'reachedAudience',
    uniqueViewers: 'uniqueViewers',
    followCount: 'follow',
  };

  submitForm() {
    this.formSubmitted.set(true);
    // if (!this.form.valid) {

    //   return;
    // }

    const dateControl = this.form.get('dateReference');
    const dateValue = dateControl?.value;

    if (!dateValue || dateControl?.invalid) {
      this.errorMessage.set('Please select a month & year.');
      dateControl?.markAsTouched();
      return;
    }

    const selectedPlatform = this.selectedPlatformName();
    const platformConfig = this.platformFields[selectedPlatform];

    if (!platformConfig) return;

    const year = String(dateValue.year);
    const month = String(dateValue.month).padStart(2, '0');

    const payload: any = {
      platformId: this.selectedPlatform(),
      productId: this.selectedProduct(),
      date: `${year}-${month}`,
    };

    platformConfig.forEach(({ control }) => {
      const apiKey = this.apiKeyMap[control];
      if (!apiKey) return;

      const rawValue = this.form.get(control)?.value;

      // WATCH TIME (string-based)
      if (control === 'watchTime') {
        if (rawValue && rawValue !== '0') {
          payload[apiKey] = rawValue;
        }
        return;
      }

      const numericValue = Number(rawValue);

      if (!isNaN(numericValue) && numericValue >= 0) {
        payload[apiKey] = numericValue;
      }
    });

    const hasValue = platformConfig.some(({ control }) => {
      const val = this.form.get(control)?.value;
      return val !== null && val !== '' && val !== undefined;
    });

    if (!hasValue) {
      this.errorMessage.set(
        'Please provide at least one non-zero metric value.'
      );
      this.formSubmitted.set(false);
      return;
    }
    const watchTimeValue = Number(payload.watchTime) || 0;
    if (this.watchTimeMode()) {
      // Hour mode: Convert H.MM to total minutes
      payload.watchTime = this.convertToMinutes(watchTimeValue);
    } else {
      // Min mode: already in minutes
      payload.watchTime = Math.round(watchTimeValue);
    }

    if (!this.isEdit()) {
      this.apiservice
        .post('/social-platform/create-stat', payload, {
          showLoader: true,
          showErrorToaster: false,
          showSuccessToaster: false,
        })
        .subscribe({
          next: (response: any) => {
            this.successMessage.set('Data submitted successfully!');
            this.isEdit.set(false);
            this.editId.set('');
            this.watchTimeMode.set(false);
            this.loadStats();
            this.overallPerformanceStats();
            this.platformWisePerformanceStats();
            this.viewsAndFollowersStats();
            this.auditLogPanel?.reloadLogs();
            setTimeout(() => {
              this.clearForm();
            }, 3000);
          },
          error: (error: any) => {
            this.errorMessage.set(
              error?.error ||
              error?.message ||
              'An error occurred while submitting the data. Please try again.'
            );
            this.formSubmitted.set(false);
          },
        });
    } else {
      payload.id = this.editId();

      this.apiservice
        .put('/social-platform/update-stat', payload, {
          showLoader: true,
          showErrorToaster: false,
          showSuccessToaster: false,
        })
        .subscribe({
          next: (response: any) => {
            this.successMessage.set('Data updated successfully!');
            this.watchTimeMode.set(false);
            this.isEdit.set(false);
            this.editId.set('');
            this.loadStats();
            this.auditLogPanel?.reloadLogs();
            this.overallPerformanceStats();
            // this.platformWisePerformanceStats();
            // this.viewsAndFollowersStats();
            setTimeout(() => {
              this.clearForm();
            }, 3000);
          },
          error: (error: any) => {
            this.errorMessage.set(
              error?.error ||
              error?.message ||
              'An error occurred while submitting the data. Please try again.'
            );
            this.formSubmitted.set(false);
          },
        });
    }

    this.form.markAllAsTouched();
  }

  clearForm() {
    this.form.reset({
      followersCount: null,
      viewCount: null,
      impressionsCount: null,
      watchTime: null,
      reachCount: null,
      engagementCount: null,
      contentInteractionCount: null,
      subscribersCount: null,
      reachAudienceCount: null,
      uniqueViewers: null,
      followCount: null,
      product: '',
      platform: '',
      dateReference: '',
    });
    this.formSubmitted.set(false);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.closePanel();
  }

  // new ui code changes

  // For multi line chart data

  hasPlatformData(label: string): boolean {
    const statsMap = this.platformChartStatsMap();
    const series = statsMap.get(label);
    return Array.isArray(series) && series.length > 0;
  }

  PLATFORM_ORDER = [
    'Instagram',
    'Facebook',
    'Pinterest',
    'Youtube',
    'Snapchat',
    'TikTok',
    'LinkedIn',
  ];

  selectedPlatformsComputed: any = computed(() => {
    return this.selectedPlatformTypeNames().map((platform) => ({
      key: platform,
      data: this.chartDataComputed()[platform],
      color: this.platformData[platform]?.color,
    }));
  });

  chartMinWidth = computed(() => {
    const platformsData = this.selectedPlatformsComputed();
    let maxDataPoints = 0;
    
    platformsData.forEach((p: any) => {
      if (p.data && p.data.length > maxDataPoints) {
        maxDataPoints = p.data.length;
      }
    });

    if (maxDataPoints > 6) {
      return `${maxDataPoints * 100}px`;
    }
    return '100%';
  });

  parsePeriod(period: string) {
    // Month: "2025-02"
    if (!period.includes(':')) {
      return {
        key: period,
        start: `${period}-01`,
      };
    }

    const [start, end] = period.split(':');

    // Year: "2025-01:2025-12"
    if (start.endsWith('-01') && end.endsWith('-12')) {
      return {
        key: start.slice(0, 4), // "2025"
        start: `${start.slice(0, 4)}-01-01`,
      };
    }

    // Quarter: "2025-01:2025-03"
    const startMonth = Number(start.split('-')[1]);
    const quarter = Math.floor((startMonth - 1) / 3) + 1;

    return {
      key: `Q${quarter} ${start.slice(0, 4)}`, // "Q1 2025"
      start: `${start}-01`,
    };
  }

  platformChartStatsMap = computed(() => {
    const response = this.platformWisePerformanceStatsData();
    const periods = response?.acrossPlatfromStats ?? [];

    const map = new Map<string, { period: string; views: number }[]>();

    periods.forEach((periodObj: any) => {
      const period = periodObj.period; // e.g. "2025-02"

      (periodObj.stats ?? []).forEach((item: any) => {
        const platform = item?.platform?.name;
        if (!platform) return;
        if (this.isSnapchatHidden() && platform === 'Snapchat') return;

        if (!map.has(platform)) {
          map.set(platform, []);
        }

        map.get(platform)!.push({
          period,
          views: Number(item?.views ?? 0),
        });
      });
    });

    return map;
  });

  hasAnyChartData = computed(() =>
    Object.values(this.chartDataComputed()).some((arr) => arr.length > 0)
  );

  chartDataComputed = computed<Record<string, any[]>>(() => {
    const selectedPlatforms = this.selectedPlatformTypeNames();
    const statsMap = this.platformChartStatsMap();

    const result: Record<string, any[]> = {};

    // 🔹 Always initialize selected platforms with empty array
    selectedPlatforms.forEach((platform) => {
      result[platform] = [];

      const series = statsMap.get(platform);
      if (!series || series.length === 0) return;

      result[platform] = series.map((item) => {
        const parsed = this.parsePeriod(item.period);

        return {
          key: platform,
          value: item.views,
          start: parsed.start,
          label: parsed.key,
          end: parsed.start,
        };
      });
    });

    return result;
  });

  // Centralized static dataset (can be replaced by API response later)
  platformData: Record<
    string,
    { key: string; values: number[]; color: string; bg: string }
  > = {
      Pinterest: {
        key: 'Pinterest',
        values: [987, 562, 435, 545, 921, 42, 4, 124, 234],
        color: '#6671EE',
        bg: '#6671EE20',
      },
      Youtube: {
        key: 'Youtube',
        values: [643, 844, 788, 64, 590, 123, 234, 456, 42],
        color: '#FF0000',
        bg: '#FF000020',
      },
      Facebook: {
        key: 'Facebook',
        values: [232, 725, 89, 905, 86, 234, 45, 24, 523],
        color: '#1877F2',
        bg: '#1877F233',
      },
      Instagram: {
        key: 'Instagram',
        values: [34, 224, 24, 224, 324, 355, 535, 234, 644],
        color: '#E4405F',
        bg: '#E4405F33',
      },
      LinkedIn: {
        key: 'LinkedIn',
        values: [564, 364, 524, 14, 764, 345, 563, 214, 455],
        color: '#9810FA',
        bg: '#9810FA20',
      },
      Snapchat: {
        key: 'Snapchat',
        values: [264, 544, 624, 143, 364, 345, 636, 354, 432],
        color: '#BCBA1E',
        bg: '#BCBA1E20',
      },
      TikTok: {
        key: 'TikTok',
        values: [764, 964, 524, 214, 664, 345, 655, 966, 242],
        color: '#F05050',
        bg: '#F0505020',
      },
    };

  get platformList() {
    const list = Object.values(this.platformData);
    if (this.isSnapchatHidden()) {
      return list.filter((p) => p.key !== 'Snapchat');
    }
    return list;
  }

  getLinesColor(platform: string) {
    return this.platformData[platform]?.color;
  }

  // For multi line chart data end

  // Platform preformance stats config

  statsConfig: any = {
    Facebook: {
      leftSection: {
        color: '#1095F4',
        bgLight: 'linear-gradient(to bottom right, #e2eefd, #eef5fe)',
        bgDark: 'linear-gradient(to bottom right, #16204f, #151f4e)',
        icon: 'img/social-icon/facebook-square.svg',
        label: 'Facebook',
      },
      rightSection: {
        bgLight: '#f3f8fe',
        bgDark: '#161b46',
        stats: [
          { key: 'follower', label: 'Followers', icon: 'user-group' },
          { key: 'reach', label: 'Viewers', icon: 'target' },
          { key: 'views', label: 'Views', icon: 'eye' },
          { key: 'watchTime', label: 'Watch Time (hrs)', icon: 'clock' },
          { key: 'follow', label: 'Follows', icon: 'user-check' },
        ],
      },
    },

    Instagram: {
      leftSection: {
        color: '#CC2F96',
        bgLight: 'linear-gradient(to bottom right, #fce7eb, #fdf0f3)',
        bgDark: 'linear-gradient(to bottom right, #2a1a40, #291940)',
        icon: 'img/social-icon/instagram-icon.svg',
        label: 'Instagram',
      },
      rightSection: {
        bgLight: '#fef5f7',
        bgDark: '#20183f',
        stats: [
          { key: 'follower', label: 'Followers', icon: 'user-group' },
          { key: 'reach', label: 'Reach', icon: 'target' },
          { key: 'views', label: 'Views', icon: 'eye' },
          {
            key: 'contentInteraction',
            label: 'Content Interactions',
            icon: 'heart',
          },
          { key: 'follow', label: 'Follows', icon: 'user-check' },
        ],
      },
    },

    LinkedIn: {
      leftSection: {
        color: '#007EBB',
        bgLight: 'linear-gradient(to bottom right, #e2eefd, #eef5fe)',
        bgDark: 'linear-gradient(to bottom right, #16204f, #161d4a)',
        icon: 'img/social-icon/linkedin-icon.svg',
        label: 'LinkedIn',
      },
      rightSection: {
        bgLight: '#f3f8fe',
        bgDark: '#161b46',
        stats: [
          { key: 'follower', label: 'Follower', icon: 'user-group' },
          { key: 'impressions', label: 'Impression', icon: 'cursor-click' },
          { key: 'views', label: 'Page Views', icon: 'eye' },
          { key: 'engagement', label: 'Engagement Rate', icon: 'trending-up' },
        ],
      },
    },

    Pinterest: {
      leftSection: {
        color: '#CB2027',
        bgLight: 'linear-gradient(to bottom right, #fcdfe3, #fdedef)',
        bgDark: 'linear-gradient(to bottom right, #2a143b, #2a143a)',
        icon: 'img/social-icon/pintrest-square-icon.svg',
        label: 'Pinterest',
      },
      rightSection: {
        bgLight: '#fef2f4',
        bgDark: '#20153c',
        stats: [
          { key: 'views', label: 'Monthly Views', icon: 'eye' },
          { key: 'impressions', label: 'Impression', icon: 'cursor-click' },
          { key: 'engagement', label: 'Engagement', icon: 'heart' },
          { key: 'reachedAudience', label: 'Total Audience', icon: 'users' },
        ],
      },
    },

    Snapchat: {
      leftSection: {
        color: '#BCBA1E',
        bgLight: 'linear-gradient(to bottom right, #ffffe0, #ffffed)',
        bgDark: 'linear-gradient(to bottom right, #333236, #272639)',
        icon: 'img/social-icon/snapchat-icon.svg',
        label: 'Snapchat',
      },
      rightSection: {
        bgLight: '#fffff2',
        bgDark: '#22213a',
        stats: [
          { key: 'views', label: 'Views', icon: 'eye' },
          { key: 'reach', label: 'Total Reach', icon: 'target' },
          { key: 'uniqueViewers', label: 'Unique Viewers', icon: 'user' },
        ],
      },
    },

    TikTok: {
      leftSection: {
        color: '#ff2d56',
        bgLight: 'linear-gradient(to bottom right, #dfdfdf, #ededed)',
        bgDark: 'linear-gradient(to bottom right, #141436, #151539)',
        icon: 'img/social-icon/tiktok-square-icon.svg',
        label: 'TikTok',
      },
      rightSection: {
        bgLight: '#f2f2f2',
        bgDark: '#15153a',
        stats: [
          { key: 'views', label: 'Views', icon: 'eye' },
          { key: 'reachedAudience', label: 'Reached Audience', icon: 'users' },
          { key: 'contentInteraction', label: 'Profile Visits', icon: 'user' },
          { key: 'engagement', label: 'Likes', icon: 'heart' },
        ],
      },
    },

    Youtube: {
      leftSection: {
        color: '#DE2925',
        bgLight: 'linear-gradient(to bottom right, #ffdfdf, #ffeded)',
        bgDark: 'linear-gradient(to bottom right, #331436, #271539)',
        icon: 'img/social-icon/youtube-icon.svg',
        label: 'Youtube',
      },
      rightSection: {
        bgLight: '#fff2f2',
        bgDark: '#22153a',
        stats: [
          { key: 'subscribers', label: 'Subscribers', icon: 'user-plus' },
          { key: 'views', label: 'Views', icon: 'eye' },
          { key: 'watchTime', label: 'Watch Time (hrs)', icon: 'clock' },
          { key: 'reachedAudience', label: 'Monthly Audience', icon: 'users' },
        ],
      },
    },
  };

  // Over all card-config

  cardStatsConfig = computed<CardStatConfig[]>(() => {
    const data = this.overallPerformanceStatsData() || {};
    return [
      {
        color: '#1095F4',
        bg: '#15B268',
        icon: 'eye',
        label: 'Total Views',
        count: data.views ?? 0,
      },
      {
        color: '#CC2F96',
        bg: '#1E6AFD',
        icon: 'users',
        label: 'Followers',
        count: data.follower ?? 0,
      },
      {
        color: '#007EBB',
        bg: '#9810FA',
        icon: 'mousePointer',
        label: 'Impressions',
        count: data.impressions ?? 0,
      },
      {
        color: '#CB2027',
        bg: '#F07F1C',
        icon: 'heart',
        label: 'Engagement',
        count: data.engagement ?? 0,
      },
      {
        color: '#BCBA1E',
        bg: '#6671EE',
        icon: 'clock',
        label: 'Watch Time (in hours)',
        count: this.convertMinToHMM(data.watchTime ?? 0) as any,
      },
      {
        color: '#ff2d56',
        bg: '#F05050',
        icon: 'users',
        label: 'Subscribers',
        count: data.subscribers ?? 0,
      },
    ];
  });

  visibleStats = computed(() => {
    const stats = this.platformWisePerformanceStatsData().performanceStats || [];
    const filtered = this.isSnapchatHidden()
      ? stats.filter((item: any) => item.platform?.name !== 'Snapchat')
      : stats;
    return filtered.slice(0, this.visibleCount());
  });

  mobilePlatformItems = computed<MobilePlatformItem[]>(() => {
    return this.visibleStats()
      .filter((item: any) => this.hasAnyStats(item))
      .map((item: any) => {
        const platformName = item.platform?.name ?? '';
        const leftSection = this.statsConfig[platformName]?.leftSection ?? {};
        const stats = this.getMobilePlatformStats(item, platformName);

        return {
          platformName,
          accentColor: leftSection.color || '#AD46FF',
          badgeBg: leftSection.bgLight || '#F5F5F5',
          icon: leftSection.icon || '',
          primaryStat: stats[0] ?? null,
          secondaryStats: stats.slice(1),
        };
      });
  });

  hasAnyStats(item: any): boolean {
    if (!item) return false;
    const metrics = [
      'views',
      'follower',
      'reach',
      'subscribers',
      'impressions',
      'engagement',
      'contentInteraction',
      'reachedAudience',
      'uniqueViewers',
    ];

    const hasNumericStats = metrics.some(
      (key) => this.toNumber(item[key]) > 0
    );
    const hasWatchTime = this.toNumber(item.watchTime) > 0;

    return hasNumericStats || hasWatchTime;
  }

  private getMobilePlatformStats(item: any, platformName: string) {
    const configStats =
      this.statsConfig[platformName]?.rightSection?.stats ?? [];

    const prioritized = configStats
      .filter((stat: any) => {
        const value = item?.[stat.key];
        return value !== null && value !== undefined && value !== 'NA';
      })
      .map((stat: any, index: number) => ({
        stat,
        index,
      }))
      .sort((a: any, b: any) => {
        const aPriority = this.getMobilePlatformStatPriority(a.stat?.key);
        const bPriority = this.getMobilePlatformStatPriority(b.stat?.key);

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        return a.index - b.index;
      })
      .map(({ stat }: any) => ({
        key: stat.key,
        label: stat.label,
        formattedValue: this.formatMobilePlatformMetricValue(item, stat.key),
      }));

    return prioritized;
  }

  private getMobilePlatformStatPriority(key: string | undefined): number {
    if (key === 'views') return 0;
    if (key === 'follower') return 1;
    return 2;
  }

  private formatMobilePlatformMetricValue(item: any, key: string): string {
    if (key === 'watchTime') {
      return `${this.convertMinToHMM(this.toNumber(item?.[key]))} hr`;
    }

    const value = this.toNumber(item?.[key]);
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: value >= 100000 ? 0 : 1,
    }).format(value);
  }

  resetPagination(): void {
    this.visibleCount.set(this.batchSize);
  }

  // scroll handler
  onScroll(event: Event): void {
    const el = event.target as HTMLElement;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

    if (!nearBottom) return;

    const current = this.visibleCount();
    const total = this.processedStatsData().length;

    // const next = Math.min(current + this.batchSize, total);
    // this.visibleCount.set(next);

    if (nearBottom) {
      if (current < total) {
        this.visibleCount.update((count) => count + this.batchSize);
      }
    }
  }

  isEdit = signal(false);
  editId = signal('');

  editStats(item: SocialPlatformStat) {
    this.isEdit.set(true);
    this.editId.set(item.id);
    const product = this.productsList().find(
      (d: DropdownOption) => d?.label == item?.productName || (!!item?.productId && d?.value == item.productId)
    );
    const platform = this.platformsList().find(
      (d: DropdownOption) => d?.label == item?.platformName
    );

    if (platform) {
      this.selectedPlatform.set(platform.value);
      this.selectedPlatformName.set(platform.label);
      this.applyPlatformFieldVisibility(platform.label);
    }

    this.panelOpen.set(true);

    let dateValue: any = null;
    if (item.date && item.date.includes('-')) {
      const parts = item.date.split('-');
      dateValue = {
        year: parseInt(parts[0], 10),
        month: parseInt(parts[1], 10),
      };
    }

    this.form.patchValue({
      product: product?.value || '',
      platform: platform?.value || '',
      dateReference: dateValue,

      followersCount: this.toNumber(item.follower),
      viewCount: this.toNumber(item.views),
      impressionsCount: this.toNumber(item.impressions),
      watchTime: this.watchTimeMode()
        ? this.convertMinToHMM(this.toNumber(item.watchTime))
        : this.toNumber(item.watchTime),

      reachCount: this.toNumber(item.reach),
      engagementCount: this.toNumber(item.engagement),
      contentInteractionCount: this.toNumber(item.contentInteraction),

      subscribersCount: this.toNumber(item.subscribers),
      reachAudienceCount: this.toNumber(item.reachedAudience),
      uniqueViewers: this.toNumber(item.uniqueViewers),
      followCount: this.toNumber(item.follow),
    });
    this.formValues.set(this.form.value);
  }

  toNumber(value: any): number {
    if (value === 'NA' || value === 'N/A' || value == null) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  }

  convertToMinutes(val: number): number {
    const hours = Math.floor(val);
    const minutes = Math.round((val - hours) * 100);
    return (hours * 60) + minutes;
  }

  convertMinToHMM(mins: number): string {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    return (hours + (minutes / 100)).toFixed(2);
  }

  watchTimeValidator(control: any) {
    const value = control.value;
    if (value === null || value === '') return null;

    const isHourMode = this.watchTimeMode();
    const valueStr = String(value);

    if (isHourMode) {
      // HOUR MODE: Must be H.MM (00-59 mins)
      const hourRegex = /^[0-9]*(\.[0-5][0-9])$/;
      const isInteger = /^[0-9]*$/.test(valueStr);
      
      // Allow pure integers (e.g. "1") or strict H.MM (e.g. "1.30")
      if (!isInteger && !hourRegex.test(valueStr)) {
        return { invalidWatchTime: true };
      }
    } else {
      // MINUTE MODE: Must be Integer only
      const minRegex = /^[0-9]*$/;
      if (!minRegex.test(valueStr)) {
        return { invalidWatchTime: true };
      }
    }
    return null;
  }

  // selectPlatformType(item: DropdownOption) {
  //   // 🚫 Guard: no data
  //   if (!this.hasPlatformData(item.label)) {
  //     this.toasterService.error(`No trend data available for ${item.label}`);
  //     return;
  //   }

  //   this.selectedPlatformTypeNames.update((selected) => {
  //     // 1️⃣ If already selected → unselect it
  //     if (selected.includes(item.label)) {
  //       const updated = selected.filter((p) => p !== item.label);

  //       // 🟢 User explicitly cleared everything
  //       if (updated.length === 0) {
  //         this.userClearedSelection.set(true);
  //       }

  //       return updated;
  //     }

  //     // User is adding → reset manual-clear flag
  //     this.userClearedSelection.set(false);

  //     // 2️⃣ If under limit → just add
  //     if (selected.length < 3) {
  //       return [...selected, item.label];
  //     }

  //     // 3️⃣ If limit reached (3) → FIFO behavior
  //     // Remove the first (oldest) and add the new one
  //     return [...selected.slice(1), item.label];
  //   });
  // }

  selectPlatformType(item: DropdownOption) {
    // 🚫 Guard: no data
    if (!this.hasPlatformData(item.label)) {
      this.toasterService.error(`No trend data available for ${item.label}`);
      return;
    }

    this.selectedPlatformTypeNames.update((selected) => {
      const isSelected = selected.includes(item.label);

      // ❌ Prevent unselecting the LAST remaining platform
      if (isSelected && selected.length === 1) {
        return selected; // do nothing
      }

      // ✅ Unselect if more than 1 selected
      if (isSelected) {
        return selected.filter((p) => p !== item.label);
      }

      // ✅ Add if under limit
      if (selected.length < 3) {
        return [...selected, item.label];
      }

      // ✅ FIFO: remove oldest, add new
      return [...selected.slice(1), item.label];
    });
  }

  // Form fields data

  selectPlatform(item: DropdownOption) {
    if (!item) return;

    const dateReference = this.form.get('dateReference')?.value;
    const product = this.form.get('product')?.value ?? '';

    this.selectedPlatform.set(item?.value);
    this.selectedPlatformName.set(item?.label);

    this.form.reset({
      followersCount: null,
      viewCount: null,
      impressionsCount: null,
      watchTime: null,
      reachCount: null,
      engagementCount: null,
      contentInteractionCount: null,
      subscribersCount: null,
      reachAudienceCount: null,
      uniqueViewers: null,
      followCount: null,
      product,
      platform: item?.value ?? '',
      dateReference: dateReference ?? '',
    });

    this.applyPlatformFieldVisibility(item.label);
    this.formValues.set(this.form.getRawValue());
  }

  platformFields: Record<
    string,
    { control: string; label: string; toggleTimeMode?: boolean }[]
  > = {
      Youtube: [
        { control: 'subscribersCount', label: 'Subscribers' },
        { control: 'viewCount', label: 'Views' },
        {
          control: 'watchTime',
          label: `Watch Time (${!this.watchTimeMode() ? 'Min' : 'Hour'})`,
          toggleTimeMode: true,
        },
        { control: 'reachAudienceCount', label: 'Monthly Audience' },
      ],

      TikTok: [
        { control: 'viewCount', label: 'Views' },
        { control: 'reachAudienceCount', label: 'Reached Audience' },
        { control: 'contentInteractionCount', label: 'Profile Visits' },
        { control: 'engagementCount', label: 'Likes' },
        // { control: "followersCount", label: "Followers" }
      ],

      Instagram: [
        { control: 'followersCount', label: 'Followers' },
        { control: 'reachCount', label: 'Reach' },
        { control: 'viewCount', label: 'Views' },
        { control: 'contentInteractionCount', label: 'Content Interactions' },
        { control: 'followCount', label: 'Follows' },
      ],

      Facebook: [
        { control: 'followersCount', label: 'Followers' },
        { control: 'reachCount', label: 'Viewers' },
        { control: 'viewCount', label: 'Views' },
        {
          control: 'watchTime',
          label: `Watch Time (${!this.watchTimeMode() ? 'Min' : 'Hour'})`,
          toggleTimeMode: true,
        },
        { control: 'followCount', label: 'Follows' },
      ],

      Pinterest: [
        { control: 'viewCount', label: 'Monthly Views' },
        { control: 'impressionsCount', label: 'Impression' },
        { control: 'engagementCount', label: 'Engagement' },
        { control: 'reachAudienceCount', label: 'Total Audience' },
      ],

      Snapchat: [
        { control: 'viewCount', label: 'Views' },
        { control: 'reachCount', label: 'Total Reach' },
        { control: 'uniqueViewers', label: 'Unique Viewers' },
      ],

      LinkedIn: [
        { control: 'followersCount', label: 'Follower' },
        { control: 'impressionsCount', label: 'Impression' },
        { control: 'viewCount', label: 'Page Views' },
        { control: 'engagementCount', label: 'Engagement Rate' },
      ],
    };

  get selectedPlatformFields() {
    return this.platformFields[this.selectedPlatformName()] || [];
  }

  applyPlatformFieldVisibility(platform: string) {
    const allowed = this.platformFields[platform]?.map((f) => f.control) || [];

    Object.keys(this.form.controls).forEach((controlName) => {
      // ALWAYS ENABLE THESE
      if (['product', 'platform', 'dateReference'].includes(controlName)) {
        this.form.get(controlName)?.enable({ emitEvent: false });
        return;
      }

      // ENABLE only fields used in this platform
      if (allowed.includes(controlName)) {
        this.form.get(controlName)?.enable({ emitEvent: false });
      } else {
        this.form.get(controlName)?.disable({ emitEvent: false });
        this.form.get(controlName)?.reset('', { emitEvent: false });
      }
    });
  }

  // Donut chart follower data

  donutData = computed(() => {
    const list = this.viewsAndFollowersStatsData() || [];

    const followersMap: any = new Map(
      list?.map((item: any) => [
        item?.platform?.name,
        Number(item?.follower ?? 0),
      ])
    );

    const data = [
      {
        label: 'Facebook',
        value: followersMap.get('Facebook') ?? 0,
        color: '#1E7BF6',
      },
      {
        label: 'Instagram',
        value: followersMap.get('Instagram') ?? 0,
        color: '#e9445dda',
      },
      {
        label: 'LinkedIn',
        value: followersMap.get('LinkedIn') ?? 0,
        color: '#0A66C2',
      },
      {
        label: 'YouTube',
        value: followersMap.get('Youtube') ?? 0,
        color: '#FF2D2D',
      },
      {
        label: 'Pinterest',
        value: followersMap.get('Pinterest') ?? 0,
        color: '#F07F1C',
      },
      {
        label: 'Snapchat',
        value: followersMap.get('Snapchat') ?? 0,
        color: '#BCBA1E',
      },
      {
        label: 'TikTok',
        value: followersMap.get('TikTok') ?? 0,
        color: '#AD46FF',
      },
    ];

    if (this.isSnapchatHidden()) {
      return data.filter((d) => d.label !== 'Snapchat');
    }
    return data;
  });

  // Top 3 watch time chart data

  topThreeWatchTimeData = computed(() => {
    const list = this.viewsAndFollowersStatsData() || [];

    const normalized = list?.map((item: any) => ({
      label: item?.platform?.name,
      value: Number((item?.watchTime ?? 0) / 60),
    }));

    const colorMap: Record<string, string> = {
      Facebook: '#1E7BF6',
      Instagram: '#E9445E',
      LinkedIn: '#0A66C2',
      Youtube: '#FF2D2D',
      Pinterest: '#E60023',
      Snapchat: '#FFFC00',
      TikTok: '#AD46FF',
    };

    const filtered = this.isSnapchatHidden()
      ? normalized.filter((item: any) => item.label !== 'Snapchat')
      : normalized;

    const topThree = filtered
      .sort((a: any, b: any) => b.value - a.value)
      .slice(0, 3);

    return topThree.map((item: any) => ({
      label: item.label,
      value: Number(item.value),
      color: colorMap[item.label] ?? '#999999',
    }));
  });

  // For multi horizontal chart data

  platformStatsMap = computed(() => {
    const list = this.viewsAndFollowersStatsData() || [];
    return new Map(
      list?.map((item: any) => [
        item?.platform?.name,
        {
          views: item?.views ?? 0,
          followers: item?.follower ?? 0,
        },
      ])
    );
  });

  multiHorizontalBarChartData = computed<MultiBarData[]>(() => {
    const map: any = this.platformStatsMap();

    let platforms = [
      'Facebook',
      'Instagram',
      'LinkedIn',
      'Pinterest',
      'Snapchat',
      'TikTok',
      'Youtube',
    ];

    if (this.isSnapchatHidden()) {
      platforms = platforms.filter((p) => p !== 'Snapchat');
    }

    return platforms.map((platform) => {
      const stats = map.get(platform) ?? { views: 0, followers: 0 };

      return {
        category: platform,
        groups: [
          {
            label: 'views',
            value: stats.views ?? 0,
            color: '#AD46FF',
          },
          {
            label: 'followers',
            value: stats.followers,
            color: '#1A79F4',
          },
        ],
      };
    });
  });

  // Overall card data api

  overallPerformanceStats(): void {
    if (!this.isSectionActive(SocialPlatformSectionId.OVERALL_STATS)) return;

    this.isOverallLoading.set(true);
    const fetchGeneration = this.dataFetchGeneration;
    const fetchProductId = this.selectedProduct();

    this.apiservice
      .get(
        `/social-platform/overall-performance-details/${this.selectedProduct()}`,
        {
          showLoader: false,
          showErrorToaster: true,
          showSuccessToaster: false,
        }
      )
      .subscribe({
        next: (response: any) => {
          const isStale = fetchGeneration !== this.dataFetchGeneration
            || fetchProductId !== this.selectedProduct();
          if (isStale) {
            this.isOverallLoading.set(false);
            return;
          }
          const apiResponse = response as ApiResponse;
          if (apiResponse) {
            this.overallPerformanceStatsData.set(apiResponse || []);
          }
          this.isOverallLoading.set(false);
        },
        error: (error: any) => {
          console.error('Error fetching stats:', error);
          this.isOverallLoading.set(false);
          this.errorMessage.set('Failed to load social platform statistics');
        },
      });
  }

  // Platform waise card data api

  platformWisePerformanceStats(): void {
    if (
      !this.isSectionActive(
        SocialPlatformSectionId.PLATFORM_PERFORMANCE,
        SocialPlatformSectionId.VIEWS_TREND,
      )
    ) {
      return;
    }

    if (!this.selectedProduct()) return;
    this.isPlatformWiseLoading.set(true);
    const fetchGeneration = this.dataFetchGeneration;
    const fetchProductId = this.selectedProduct();

    let payload: any = {
      productId: this.selectedProduct(),
      unit: this.unit(),
    };
    if (this.selectedDateRange()?.start && this.selectedDateRange()?.end) {
      (payload.startDate = this.selectedDateRange()?.start ?? ''),
        (payload.endDate = this.selectedDateRange()?.end ?? '');
    }

    this.apiservice
      .get(`/social-platform/platform-performance-details`, payload)
      .subscribe({
        next: (response: any) => {
          const isStale = fetchGeneration !== this.dataFetchGeneration
            || fetchProductId !== this.selectedProduct();
          if (isStale) {
            this.isPlatformWiseLoading.set(false);
            return;
          }
          const apiResponse = response as ApiResponse;

          if (apiResponse) {
            this.platformWisePerformanceStatsData.set(apiResponse || []);
            this.isPlatformWiseLoading.set(false);
          }
        },
        error: (error: any) => {
          console.error('Error fetching stats:', error);
          this.isPlatformWiseLoading.set(false);
          this.errorMessage.set('Failed to load social platform statistics');
        },
      });
  }

  // Views and Followers chart data api

  viewsAndFollowersStats(): void {
    if (
      !this.isSectionActive(
        SocialPlatformSectionId.OVERALL_ANALYTICS,
        SocialPlatformSectionId.WATCH_TIME,
        SocialPlatformSectionId.FOLLOW_BY_PLATFORM,
      )
    ) {
      return;
    }

    this.isDonutChartLoading.set(true);
    const fetchGeneration = this.dataFetchGeneration;
    const fetchProductId = this.selectedProduct();
    this.apiservice
      .get(`/social-platform/overall-analytics/${this.selectedProduct()}`)
      .subscribe({
        next: (response: any) => {
          const isStale = fetchGeneration !== this.dataFetchGeneration
            || fetchProductId !== this.selectedProduct();
          if (isStale) {
            this.isDonutChartLoading.set(false);
            return;
          }
          const apiResponse = response as ApiResponse;
          if (apiResponse) {
            this.viewsAndFollowersStatsData.set(apiResponse || []);
            this.isDonutChartLoading.set(false);
          }
        },
        error: (error: any) => {
          console.error('Error fetching stats:', error);
          this.isDonutChartLoading.set(false);
          this.errorMessage.set('Failed to load social platform statistics');
        },
      });
  }

  // Toggle mode for watch time
  // Toggle mode for watch time (Min <-> Hour)
  toggleTimeMode(event: any, field: any) {
    const currentValue = this.form.get('watchTime')?.value;
    const isHour = event.checked;

    this.watchTimeMode.set(isHour);

    if (currentValue && !isNaN(Number(currentValue))) {
      const num = Number(currentValue);
      if (isHour) {
        // Min -> Hour.Minutes (e.g. 90 -> 1.30)
        this.form.get('watchTime')?.setValue(this.convertMinToHMM(num));
      } else {
        // Hour.Minutes -> Min (e.g. 1.30 -> 90)
        this.form.get('watchTime')?.setValue(this.convertToMinutes(num));
      }
    }

    this.selectedPlatformFields.forEach((field) => {
      if (field.toggleTimeMode) {
        field.label = `Watch Time (${!this.watchTimeMode() ? 'Min' : 'Hour'})`;
      }
    });
  }

  LA_TIMEZONE = 'America/Los_Angeles';

  // Helper function to get current time in LA timezone
  nowInLA = () => dayjs().tz(this.LA_TIMEZONE);
  currentYear = this.nowInLA().year();
  currentMonth = this.nowInLA().month() + 1;

  get selectedStartRange() {
    return this.nowInLA().subtract(89, 'day').startOf('day').toISOString();
  }

  get selectedEndRange() {
    return this.nowInLA().endOf('day').toISOString();
  }

  predefinedRanges: PredefinedRange[] = [
    {
      label: 'Last 90 Days',
      getValue: () => ({
        start: this.nowInLA().subtract(89, 'day').startOf('day').toISOString(),
        end: this.nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'This Quarter',
      getValue: () => {
        const now = this.nowInLA();
        const quarter = Math.floor(now.month() / 3);
        const quarterStart = now.month(quarter * 3).startOf('month');
        return {
          start: quarterStart.toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Quarter',
      getValue: () => {
        const now = this.nowInLA().subtract(3, 'month');
        const quarter = Math.floor(now.month() / 3);
        const quarterStart = now.month(quarter * 3).startOf('month');
        const quarterEnd = now.month(quarter * 3 + 2).endOf('month');
        return {
          start: quarterStart.toISOString(),
          end: quarterEnd.toISOString(),
        };
      },
    },
    {
      label: 'This Year',
      getValue: () => {
        const now = this.nowInLA();
        return {
          start: now.startOf('year').toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Year',
      getValue: () => {
        const lastYear = this.nowInLA().subtract(1, 'year');
        return {
          start: lastYear.startOf('year').toISOString(),
          end: lastYear.endOf('year').toISOString(),
        };
      },
    },
  ];
}
